/**
 * `readDoc` -- the one way a store should read a single Firestore document.
 *
 * `getDoc()` goes to the server whenever the client is online; Firestore's
 * on-disk (IndexedDB) cache from `persistentLocalCache` is only its offline
 * fallback. A store that wants its own freshness window (lookups once a day,
 * students once an hour, ...) has to ask for the cached copy explicitly with
 * `getDocFromCache()` -- otherwise "fresh enough" never saves a read, because
 * every page load starts with empty memory and `getDoc` doesn't know the
 * store considers yesterday's copy good enough.
 *
 * This only reads; it has no opinion on *how long* a store's data stays
 * fresh. That rule -- and the timestamp it is checked against -- stays next
 * to each store, per docs/local-caching-TODO.md section 4: the window
 * differs per store (4am Eastern for lookups, an hour for students, a day
 * for programs) and the reason for each window belongs in a comment there,
 * not buried in shared plumbing. `onServerRead` is how a store stamps that
 * timestamp -- it fires only when a real server read happened, never on a
 * cache hit, or the cache would never expire.
 *
 * Deliberately not wrapped with `reportClientError` the way `getDocs` is in
 * `$lib/firestore/reads.ts`: a single-document read can't fail for want of a
 * missing index, and a denied read is usually a deliberate access boundary.
 * See that file's comment for the reasoning this mirrors.
 */

import {
	getDoc,
	getDocFromCache,
	type DocumentData,
	type DocumentReference,
	type DocumentSnapshot
} from 'firebase/firestore';

export type ReadDocOptions = {
	/**
	 * True when the caller's own freshness rule says the last server read is
	 * recent enough to trust whatever Firestore already has on disk.
	 */
	isFresh: boolean;
	/**
	 * Called once a server read completes successfully. Never called for a
	 * cache hit. Use it to stamp the freshness timestamp this `isFresh` was
	 * computed from.
	 */
	onServerRead?: () => void;
	/** Skip the cache and the freshness check; always read the server. */
	force?: boolean;
};

export async function readDoc<AppModelType, DbModelType extends DocumentData>(
	ref: DocumentReference<AppModelType, DbModelType>,
	{ isFresh, onServerRead, force = false }: ReadDocOptions
): Promise<DocumentSnapshot<AppModelType, DbModelType>> {
	if (!force && isFresh) {
		try {
			return await getDocFromCache(ref);
		} catch {
			// Nothing cached locally yet (cleared site data, a new profile, or a
			// doc that has never been read directly) -- fall through to the
			// server below.
		}
	}
	const snap = await getDoc(ref);
	onServerRead?.();
	return snap;
}
